import { useCallback, useMemo } from 'react';
import { Box, Grid, Typography, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import Chart from '@/components/general/Chart';
import PageTitle from '@/components/general/PageTitle';
import { getEndpointWithParams } from '@/routes/config';
import { Trend } from '@/types/api';
import { Paths } from '@/types/routes';
import { dateToMonthDayFormat } from '@/utils/format';
import styles from './TrendsReportList.styles';

type TrendsReportListPageProps = {
  trends: Trend[];
};

const TrendsReportListPage = function ({ trends }: TrendsReportListPageProps): JSX.Element {
  const getTrendDetailRoute = useCallback(
    (questionId: string): string =>
      getEndpointWithParams(Paths.TRENDS_REPORT_DETAIL, {
        questionId,
      }),
    []
  );

  const renderTrendsCharts = useMemo(() => {
    return trends.map((trend) => {
      const labels =
        trend.answersWeek?.map(
          (answerWeek) =>
            `${dateToMonthDayFormat(answerWeek.startDate)} - ${dateToMonthDayFormat(
              answerWeek.endDate
            )}`
        ) || [];

      const data = trend.answersWeek?.map((answerWeek) => answerWeek.answerAvg) || [];

      return (
        <Grid key={trend.id} item xs={12} md={6}>
          <Chart labels={labels} data={data} title={trend.label}>
            <Box sx={styles.detailContainer}>
              <Typography>Showing the last 6 months</Typography>
              <Button component={RouterLink} variant="outlined" to={getTrendDetailRoute(trend.id)}>
                View details
              </Button>
            </Box>
          </Chart>
        </Grid>
      );
    });
  }, [getTrendDetailRoute, trends]);

  return (
    <Box data-testid="rotating-questions-list-page">
      <PageTitle title="Trends Report" />

      <Grid container spacing={2} marginTop={1}>
        {trends.length < 1 && (
          <Grid item md={12}>
            <Typography variant="h6" color="primary.light">
              No trends data yet
            </Typography>
          </Grid>
        )}

        {renderTrendsCharts}
      </Grid>
    </Box>
  );
};

export default TrendsReportListPage;
